import { CaseParameters } from "./CaseModel";

// Exhaustive by construction: TypeScript requires exactly the keys of
// CaseParameters here — a missing or extra key fails to compile. This is the
// single source of truth the persistence loop iterates.
const CASE_PARAMETERS_FIELDS: { [key in keyof CaseParameters]: true } = {
  id: true,
  name: true,
  claimKind: true,
  timeline: true,
  bookedReserves: true,
  judgment: true,
  closing: true,
  agreements: true,
  budgets: true,
  claims: true,
  damages: true,
  costStructure: true,
  parties: true,
  settlements: true,
  settlementChances: true,
};
export const CASE_PARAMETERS_KEYS: (keyof CaseParameters)[] = Object.keys(
  CASE_PARAMETERS_FIELDS,
) as (keyof CaseParameters)[];

export const CASE_EXPORT_APP = "casesolv";
export const CASE_EXPORT_TYPE = "case-export";
export const CASE_EXPORT_VERSION = 1;

/**
 * Encrypted, on-disk envelope produced by export. Carries marker fields so
 * import can reject foreign/corrupt files, plus the parameters needed to
 * re-derive the key and decrypt. `data` is the AES-GCM ciphertext of
 * `JSON.stringify(CaseParameters)`. All binary fields are base64.
 */
export interface CaseExportFile {
  app: typeof CASE_EXPORT_APP;
  type: typeof CASE_EXPORT_TYPE;
  version: number;
  kdf: {
    name: "PBKDF2";
    salt: string;
    iterations: number;
    hash: "SHA-256";
  };
  cipher: {
    name: "AES-GCM";
    iv: string;
  };
  data: string;
}

/** Pick only the parameter fields, dropping all computed output, and fill defaults. */
export function toCaseParameters({
  id,
  name,
  claimKind,
  timeline,
  bookedReserves,
  judgment,
  closing,
  parties = [],
  claims = [],
  damages = [],
  budgets = [],
  costStructure = {},
  settlements = [],
  settlementChances,
  agreements = [],
}: CaseParameters): CaseParameters {
  return {
    id,
    name,
    ...(claimKind && { claimKind }),
    ...(timeline && { timeline }),
    ...(bookedReserves && { bookedReserves }),
    ...(judgment && { judgment }),
    ...(closing && { closing }),
    parties,
    claims,
    damages,
    budgets,
    costStructure,
    settlements,
    ...(settlementChances && { settlementChances }),
    agreements,
  };
}

/** Structural check that an unknown object is one of our export envelopes. */
export function isCaseExportFile(value: unknown): value is CaseExportFile {
  if (typeof value !== "object" || value === null) return false;
  const f = value as Record<string, unknown>;
  return (
    f.app === CASE_EXPORT_APP &&
    f.type === CASE_EXPORT_TYPE &&
    typeof f.data === "string" &&
    typeof f.kdf === "object" &&
    f.kdf !== null &&
    typeof f.cipher === "object" &&
    f.cipher !== null
  );
}
